import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import { supabase } from './lib/supabase';
import { getCurrentManager } from './services/authService';
// App Version: 2.0 - 85+ Languages Support

// Lazy load pages for better performance
const Landing = lazy(() => import('./pages/Landing'));
const EmployeePortal = lazy(() => import('./pages/EmployeePortal'));
const EmployeeLogin = lazy(() => import('./pages/EmployeeLogin'));
const EmployeeRegister = lazy(() => import('./pages/EmployeeRegister'));
const ManagerLogin = lazy(() => import('./pages/ManagerLogin'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const TeamManagement = lazy(() => import('./pages/TeamManagement'));
const Settings = lazy(() => import('./pages/Settings'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const Pricing = lazy(() => import('./pages/Pricing'));
const ClimateSurvey = lazy(() => import('./pages/ClimateSurvey'));

// Admin pages
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminCompanies = lazy(() => import('./pages/AdminCompanies'));
const AdminAnalytics = lazy(() => import('./pages/AdminAnalytics'));
const AdminFeedback = lazy(() => import('./pages/AdminFeedback'));
const AdminCompose = lazy(() => import('./pages/AdminCompose'));
const AdminAnnouncements = lazy(() => import('./pages/AdminAnnouncements'));
const AdminAuditLogs = lazy(() => import('./pages/AdminAuditLogs'));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
      <p className="text-slate-600">Loading...</p>
    </div>
  </div>
);

// Auth Context
export const AuthContext = React.createContext();

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [manager, setManager] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        fetchManager();
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) {
        fetchManager();
      } else {
        setManager(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchManager = async () => {
    try {
      const managerData = await getCurrentManager();
      setManager(managerData);
    } catch (error) {
      console.error('Failed to fetch manager', error);
    } finally {
      setLoading(false);
    }
  };

  const login = (newSession) => {
    setSession(newSession);
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setManager(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" style={{ width: '48px', height: '48px', borderWidth: '4px' }} />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ session, manager, login, logout, fetchManager }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { session } = React.useContext(AuthContext);
  return session ? children : <Navigate to="/manager/login" />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <div className="App">
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              
              {/* Employee Routes */}
              <Route path="/employee" element={<EmployeePortal />} />
              <Route path="/feedback" element={<EmployeePortal />} />
              <Route path="/employee/login" element={<EmployeeLogin />} />
              <Route path="/employee/register" element={<EmployeeRegister />} />
              
              {/* Manager Routes */}
              <Route path="/register" element={<Register />} />
              <Route path="/manager/login" element={<ManagerLogin />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/demo" element={<Dashboard demo />} />
              <Route path="/survey/:surveyId" element={<ClimateSurvey />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/team"
                element={
                  <ProtectedRoute>
                    <TeamManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/companies" element={<AdminCompanies />} />
              <Route path="/admin/analytics" element={<AdminAnalytics />} />
              <Route path="/admin/feedback" element={<AdminFeedback />} />
              <Route path="/admin/compose" element={<AdminCompose />} />
              <Route path="/admin/announcements" element={<AdminAnnouncements />} />
              <Route path="/admin/audit-logs" element={<AdminAuditLogs />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;